/**
 * Storico release applicativa (PanifyCloud).
 *
 * Deploy: aggiungi una NUOVA voce in CIMA all'array, incrementa il patch (1.0.0 → 1.0.1),
 * imposta deployDate (YYYY-MM-DD) e l'elenco items. Le versioni più vecchie restano sotto.
 */

/** @typedef {{ version: string, deployDate: string, title?: string, items: string[] }} ReleaseEntry */

/** Dal più recente al più vecchio */
export const RELEASE_HISTORY = /** @type {ReleaseEntry[]} */ ([
  {
    version: '1.0.8',
    deployDate: '2026-09-24',
    title: 'Quantità in kg a 3 decimali',
    items: [
      'Planning e ordini personalizzati: le quantità in kg si gestiscono a 3 decimali. Con un passo quantità a 3 decimali (es. 0,125) il valore non viene più arrotondato a 2 e non risulta più fuori range.',
    ],
  },
  {
    version: '1.0.7',
    deployDate: '2026-09-18',
    title: 'Menu admin dopo Accedi e card planning su mobile',
    items: [
      'Amministratore: Accedi a una destinazione non toglie più Destinazioni, Prodotti, Preordini, Smistamento e Impostazioni: il menu extra resta fino al logout.',
      'Planning mobile: sulla card la quantità è un tasto (stessa altezza su tutte). Nel planning settimanale apre la modifica; sull\'ordine personalizzato apre il menu a tendina. Nota ed Elimina sono icone affiancate.',
      'Planning mobile: card a due colonne allineate, immagine a destra 80×60 senza stirarla né farla uscire. Aggiungi prodotto, Nascondi articoli a 0 e Visualizza promozioni stanno su una riga, senza scorrimento.',
      'Ordine personalizzato: Annulla, Salva e Torna al planning restano in alto su una sola riga, nella stessa larghezza della pagina.',
    ],
  },
  {
    version: '1.0.6',
    deployDate: '2026-09-18',
    title: 'Note sugli ordini straordinari e «Ricordami» al logout',
    items: [
      'Planning (ordini straordinari): su ogni articolo puoi aggiungere o modificare una nota. Al salvataggio va in descrizione come «Nota: …», entro i 150 caratteri del campo; la ricerca trova anche il testo della nota.',
      'Login: il flag «Ricordami» sopravvive al logout. Email e password restano precompilate finché non accedi di nuovo con il flag spento.',
    ],
  },
  {
    version: '1.0.5',
    deployDate: '2026-07-10',
    title: 'Allineamento layout desktop e orario blocco destinazione',
    items: [
      'Desktop: sidebar allineata sotto la navbar tramite altezza condivisa (--navbar-height), senza sovrapposizione ai pixel.',
      'Planning: corretta la logica di blocco ordini per domani con override destinazione (orarioblocco_dest); gli orari notturni tra 00:00 e 08:00 non bloccano più durante la giornata (es. blocco 00:30 alle 22:00).',
      'Login: rifinitura grafica in dark mode (form, pannello novità versione e modale recupero password) con stile coerente al resto dell\'app.',
    ],
  },
  {
    version: '1.0.4',
    deployDate: '2026-07-08',
    title: 'Duplica ordine, blocco fascia domani e dissolvenza barra di ricerca',
    items: [
      'Planning (ordini straordinari): nuovo pulsante «Duplica da altro giorno» che apre un calendario con i giorni precedenti che hanno un ordine; alla selezione la tabella viene compilata con i prodotti di quel giorno senza salvare, così puoi ancora modificare o aggiungere articoli.',
      'Planning: aggiungendo un articolo dalla tabella «Seleziona articoli», la quantità iniziale viene precompilata con la quantità minima (ordine minimo) dell\'articolo.',
      'Planning: superata l\'ora consentita, resta modificabile tutto il planning ma la colonna «domani» viene bloccata (blocco granulare); gli ordini straordinari per domani restano completamente bloccati.',
      'Planning e Documenti: barra di ricerca/filtri fissa in alto con la lista che sfuma in dissolvenza a gradiente mentre scorre sotto, per un aspetto più gradevole.',
    ],
  },
  {
    version: '1.0.3',
    deployDate: '2026-06-19',
    title: 'Layout mobile pagine admin e miglioramenti filtri',
    items: [
      'Pagine admin (Destinazioni, Magazzino, Lista produzione, Smistamento): titolo e filtri fissi in alto; solo le card/liste scrollano sotto (layout mobile unificato).',
      'Magazzino: barra inferiore admin come Destinazioni (Destinazioni · Prodotti · Preordini · Smistamento).',
      'Magazzino, Preordini e Smistamento: «Filtri attivi» e badge affiancati ai toggle filtro.',
      'Lista produzione (Preordini): quantità in evidenza in alto a destra accanto al nome prodotto nelle card mobile.',
      'Login: logo PanifyCloud di fallback se il logo azienda manca o è quasi tutto bianco.',
    ],
  },
  {
    version: '1.0.2',
    deployDate: '2026-06-19',
    title: 'Planning senza planning settimanale e blocco inserimento prodotti',
    items: [
      'Planning (PANIFYNOPLANNING=1): il clic su un giorno della settimana apre direttamente la data specifica più vicina (ordine straordinario), senza leggere il planning settimanale; salva/annulla restano sulla data selezionata.',
      'Planning (date specifiche): disabilitato il tasto Inserisci per articoli bloccati dal tempo minimo di produzione (orebloccoprod), sia negli articoli in evidenza sia nel modale Aggiungi prodotti.',
    ],
  },
  {
    version: '1.0.1',
    deployDate: '2026-05-29',
    title: 'Correzioni Smistamento e formattazione quantità',
    items: [
      'Smistamento: corretta visualizzazione quantità in pezzi (es. 10 Pz non più mostrato come 1) nella colonna Quantità.',
      'Smistamento: separatore decimale italiano (virgola) per le quantità con decimali (es. 10,5 kg).',
    ],
  },
  {
    version: '1.0.0',
    deployDate: '2026-05-12',
    title: 'Release iniziale documentata',
    items: [
      'Immagini prodotto: proxy /images verso il server tenant in sviluppo; fallback breadIcon.jpg; catena URL (tenant, immagine gestionale, /images/prodotti).',
      'Spaces: caricamento immagini da REACT_APP_DO_SPACES_URL (non CDN); risoluzione tenant in dev tramite header X-Forwarded-Host.',
      'Planning: in fascia oraria la colonna “domani” resta non editabile nel modale Modifica articolo anche se il giorno selezionato è diverso; icona punto esclamativo anche per il blocco fascia.',
      'Multi-tenant: correzione Host su richieste proxate per /client-config e allineamento config (es. Spaces) con la SPA su porta 3000.',
    ],
  },
]);

export function getCurrentRelease() {
  return RELEASE_HISTORY[0] ?? null;
}

/** Versione corrente (prima voce dello storico) */
export const APP_VERSION = getCurrentRelease()?.version ?? '0.0.0';
